<template>
  <b-card :title="$t('labels.users')">

    <b-overlay :show="loadingData" variant="transparent" no-wrap />

    <edit-user @refreshUserList="getUsers()" ref="modalEditUserC"></edit-user>

    <transfer ref="transferUser"></transfer>

    <!-- search input -->
    <div class="custom-search d-flex justify-content-end">
      <b-form-group>
        <div class="d-flex align-items-center">

          <label class="mr-1">{{$t('labels.search')}}</label>

          <b-form-input
            v-model="searchTerm"
            :placeholder="$t('labels.search')"
            type="text"
            class="d-inline-block"
          />

          <b-button v-if="$can('create','User')"
                    class="text-nowrap ml-2"
                    variant="primary"
                    @click="$refs.addUpdateUser.modalShow = true"
          >
            <feather-icon icon="PlusIcon" class="mr-50" />
            <span class="text-nowrap">{{ $t('labels.add') }}</span>
          </b-button>

          <add-update-user ref="addUpdateUser" />
        </div>
      </b-form-group>
    </div>

    <!-- table -->
    <div class="table-responsive">
      <vue-good-table
        :columns="defineColumnsByAbilities()"
        :rows="rows"
        :search-options="{
        enabled: true,
        externalQuery: searchTerm }"
        :pagination-options="{
        enabled: true,
        perPage:pageLength
        }"
        class="table">

        <template slot="table-row" slot-scope="props">

          <!-- Column: Action -->
          <span v-if="props.column.field === 'action'">
            <span>
              <b-dropdown
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
              >
                <template v-slot:button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="text-body align-middle mr-25"
                  />
                </template>

                <b-dropdown-item v-if="$can('edit','User')"
                                 v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                                 @click="editUser(props.row)"
                                 variant="outline-primary">
                  <feather-icon
                          icon="Edit2Icon"
                          class="mr-50"
                  />
                  <span>{{ $t("labels.edit") }}</span>
                </b-dropdown-item>

                <b-dropdown-item v-if="userWithWallet(props.row)"
                                 :to="{ name: 'wallets-detail', params: { id: props.row.wallet } }">
                  <feather-icon
                          icon="CreditCardIcon"
                          class="mr-50"
                  />
                  <span>{{ $t("labels.seeWallet") }}</span>
                </b-dropdown-item>

                <b-dropdown-item :to="{ name: 'users-detail', params: { id: props.row._id } }">
                  <feather-icon
                          icon="UserIcon"
                          class="mr-50"
                  />
                  <span>{{ $t("labels.detailView") }}</span>
                </b-dropdown-item>

                <b-dropdown-item v-if="$can('delete','User')"
                                 @click="deleteUser(props.row._id)">
                  <feather-icon
                    icon="TrashIcon"
                    class="mr-50"
                  />
                  <span>{{ $t("labels.delete") }}</span>
                </b-dropdown-item>

              </b-dropdown>
            </span>
          </span>

          <span v-if="props.column.field === 'status'">
            <span :style="{ color : props.row.status  ? '#28a745' : '#dc3545' }">
              {{ getStatus(props.row.status) }}
            </span>
          </span>

          <span v-if="props.column.field === 'role'">
            {{ props.row.role }}
          </span>

          <!-- Column: Common -->
          <span v-else>
          {{ props.column.field === 'status' ? '' : props.formattedRow[props.column.field] }}
          </span>

          <!-- Column: Recharge -->
          <span v-if="props.column.field === 'recharge'">

           <div class="d-flex justify-content-center">

            <b-button :disabled="!$can('recharge','User') || !userWithWallet(props.row)"
                      variant="outline-success" class="btn-icon rounded-circle mr-1"
                      v-b-tooltip.hover.top="$t('labels.credit')"
                      @click="transferUser(props.row, 'creditUser')" size="sm">
              <feather-icon icon="PlusIcon" />
            </b-button>

            <b-button :disabled="!$can('recharge','User') || !userWithWallet(props.row) || userAgent() "
                      variant="outline-danger" class="btn-icon rounded-circle"
                      v-b-tooltip.hover.top="$t('labels.debit')"
                      @click="transferUser(props.row, 'debitUser')"
                      size="sm">
              <feather-icon icon="MinusIcon" />
            </b-button>

           </div>

          </span>

        </template>

        <div slot="emptystate" class="m-2 d-flex justify-content-center">
          No users to display...
        </div>

        <!-- pagination -->
        <template
          slot="pagination-bottom"
          slot-scope="props"
        >
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap ">
                Showing 1 to
              </span>
              <b-form-select
                v-model="pageLength"
                :options="['20', '40', '60', '100']"
                class="mx-1"
                @input="(value)=>props.perPageChanged({currentPerPage:value})"
              />
              <span class="text-nowrap"> of {{ props.total }} entries </span>
            </div>
            <div>
              <b-pagination
                :value="1"
                :total-rows="props.total"
                :per-page="pageLength"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="(value)=>props.pageChanged({currentPage:value})"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>
    </div>

  </b-card>
</template>

<script>
  import {
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BDropdown,
    BDropdownItem,
    BForm,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BOverlay,
    BPagination,
    VBModal, VBTooltip,
  } from 'bootstrap-vue'
  import { VueGoodTable } from 'vue-good-table'
  import moment from 'moment'
  import { mapActions } from 'vuex'
  import Ripple from 'vue-ripple-directive'
  import vSelect from 'vue-select'
  import addUpdateUser from './addUpdateUser'
  import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
  import EditUser from './EditUser'
  import Transfer from '../shared/Transfer'
  import ability from '../../../libs/acl/ability'

  export default {
  components: {
    Transfer,
    EditUser,
    VueGoodTable,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    VBModal,
    BForm,
    BButton,
    vSelect,
    BFormCheckbox,
    BCard,
    addUpdateUser,
    BOverlay,
    ToastificationContent
  },

  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
    Ripple,
  },

  data() {
    return {
      loadingData: false,
      formData: {
        name: null,
        lastName: null,
        userName: null,
        email: null,
        status: null,
        role: [],
      },
      pageLength: 20,
      dir: false,
      columns: [
        {
          label: 'client',
          field: 'client.name',
        },
        {
          label: 'userName',
          field: 'userName',
        },
        {
          label: 'firstName',
          field: 'firstName',
        },
        // {
        //   label: 'email',
        //   field: 'email',
        // },
        {
          label: 'status',
          field: 'status',
        },
        {
          label: 'role',
          field: 'role',
        },
        // {
        //   label: "date",
        //   field: "createdAt",
        //   formatFn: this.formatCreatedAt,
        // },
        {
          label: "recharge",
          field: "recharge",
        },
        {
          label: 'action',
          field: 'action',
        }
      ],
      searchTerm: '',
    }
  },

  computed: {
    rows() {
      return this.$store.state.user.users;
    },
  },

  created() {
    this.getUsers()
  },

  methods: {
    ...mapActions('user', ['users', 'destroy', 'update']),
    ...mapActions('role', ['roles']),

    userAgent(){
      let user = JSON.parse(localStorage.getItem('userData'))
      return user.role.toUpperCase() == 'AGENT'
    },

    formatCreatedAt: function(value) {
      return moment(String(value)).format('DD-MM-YYYY HH:MM')
    },

    async deleteUser(id) {
      this.$swal({
        title: this.$t('labels.areYouSure'),
        text: this.$t('labels.notRevert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('labels.yesDelete'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      .then(async result => {
        if (result.value) {
          let response = await this.destroy(id)
          if(response){
            this.getUsers()
            this.$swal({
              icon: 'success',
              title: this.$t('labels.deleted'),
              text: response.message,
              customClass: {
                confirmButton: 'btn btn-success',
              },
            })
          }
        }
      })
    },

    async getUsers() {
      try {
        this.loadingData = true;
        await this.$store.dispatch("user/users");
      } catch (error) {
        console.log(error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('labels.errorFetchingUserList'),
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error.response ? error.response.data.message : '',
          },
        });
      } finally {
        this.loadingData = false;
      }
    },

    getStatus(status){
      return status ? 'Active' : 'Unactive'
    },

    async editUser(value){
      this.formData = value
      delete this.formData.password
      await this.$refs.modalEditUserC.showModal(this.formData)
    },

    userWithWallet(user){
      let userWithWalletFlag = false
      if(user.hasOwnProperty('wallet')){
        userWithWalletFlag = true
      }
      return userWithWalletFlag
    },

    transferUser(destinationUser,transactionType) {
      let user = JSON.parse(localStorage.getItem('userData'))

      const fromWallet = user.client.wallet
      const toWallet = destinationUser.wallet

      const entityFrom = {entity: user._id , type : 'USER'}

      const entityTo = {entity: destinationUser._id , type : 'USER' , name : '@'+destinationUser.userName}

      this.$refs.transferUser.showTransactionModal(fromWallet,toWallet,entityFrom,entityTo,transactionType)

    },

    defineColumnsByAbilities(){
      const baseColumns = [
        { label: this.$t("tableHeader.client"), field: 'client.name', action : 'read' , resource : 'User' },
        { label: this.$t("tableHeader.userName"), field: 'userName', action : 'read' , resource : 'User' },
        { label: this.$t("tableHeader.firstName"), field: 'firstName', action : 'read' , resource : 'User' },
        { label: this.$t("tableHeader.status"), field: 'status', action : 'read' , resource : 'User' },
        { label: this.$t("tableHeader.role"), field: 'role', action : 'read' , resource : 'User' },
        { label: this.$t("tableHeader.recharge"), field: "recharge", action : 'recharge' , resource : 'User' },
        { label: this.$t("tableHeader.actions"), field: 'action', action : 'read' , resource : 'User' }
      ]
      return baseColumns.filter(column => ability.can(column.action ,column.resource ))
    }

  },
}
</script>

<style lang="scss" >
  @import '@core/scss/vue/libs/vue-good-table.scss';
</style>